import ExcelJS from 'exceljs';
import { FormData } from '../types/formTypes';

// ログを保存する関数
function logToFile(message: string) {
  const timestamp = new Date().toISOString();
  const logMessage = `[${timestamp}] ${message}\n`;

  // ブラウザでは直接ファイルに書き込めないので、コンソールログとローカルストレージに保存
  console.log(message);

  // ローカルストレージにログを蓄積
  const existingLogs = localStorage.getItem('excelUtils_logs') || '';
  localStorage.setItem('excelUtils_logs', existingLogs + logMessage);
}

// ログをダウンロードする関数（エクスポート）
export function downloadLogs() {
  const logs = localStorage.getItem('excelUtils_logs') || '';
  const blob = new Blob([logs], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'excel-utils-logs.txt';
  a.click();
  URL.revokeObjectURL(url);
}

// 職業訓練実施計画届のセルマッピング（修正指示スクリーンショットに基づく修正）
const TRAINING_PLAN_CELLS = {
  // 基本情報
  // 事業主情報（スクリーンショット確認：事業主欄）
  companyNameMain: { row: 10, col: 35 }, // AI10 - 事業主：名称
  companyAddressMain: { row: 10, col: 45 }, // AS10 - 事業主：所在地

  // 事業主の詳細情報（新しいスクリーンショットに基づく）
  businessOwnerPostalCode1: { row: 9, col: 33 }, // AG9 - 事業主郵便番号前半
  businessOwnerPostalCode2: { row: 9, col: 38 }, // AL9 - 事業主郵便番号後半
  businessOwnerCompanyName: { row: 12, col: 32 }, // AF12 - 事業主名称
  businessOwnerRepresentativeName: { row: 13, col: 32 }, // AF13 - 代表者氏名
  businessOwnerCorporateNumber: { row: 14, col: 32 }, // AF14 - 法人番号

  // 雇用保険適用事業所情報
  companyName: { row: 26, col: 13 }, // M26 - 雇用保険適用事業所の名称
  // 雇用保険適用事業所番号を3つに分割（修正指示①）
  insuranceNumber1: { row: 26, col: 40 }, // AN26 - 雇用保険適用事業所番号（4桁）
  insuranceNumber2: { row: 26, col: 45 }, // AS26 - 雇用保険適用事業所番号（6桁）
  insuranceNumber3: { row: 26, col: 52 }, // AZ26 - 雇用保険適用事業所番号（1桁）
  postalCode1: { row: 27, col: 14 }, // N27 - 郵便番号前半（〒の後）
  postalCode2: { row: 27, col: 19 }, // S27 - 郵便番号後半（-の後）
  address: { row: 28, col: 13 }, // M28 - 事業所所在地

  // 担当者情報
  contactName: { row: 29, col: 25 }, // Y29 - 担当者氏名
  contactDepartment: { row: 29, col: 39 }, // AI29 - 部署（AM列との重複を避ける）
  // 電話番号を3つに分割（R30、W30、AB30）
  contactPhone1: { row: 30, col: 18 }, // R30 - 電話番号1部目
  contactPhone2: { row: 30, col: 23 }, // W30 - 電話番号2部目
  contactPhone3: { row: 30, col: 28 }, // AB30 - 電話番号3部目
  contactEmail: { row: 30, col: 39 }, // AM30 - MAIL欄（AM = 39列目）

  // 訓練情報（スクリーンショット2枚目参照）
  trainingCourseName: { row: 41, col: 13 }, // M41 - 訓練コースの名称（黄色セル）
  // 訓練実施期間（初日）を年月日に分割
  trainingStartYear: { row: 42, col: 14 }, // N42 - 訓練実施期間（初日）年
  trainingStartMonth: { row: 42, col: 20 }, // T42 - 訓練実施期間（初日）月
  trainingStartDay: { row: 42, col: 26 }, // Z42 - 訓練実施期間（初日）日
  // 訓練実施期間（最終日）を年月日に分割
  trainingEndYear: { row: 42, col: 35 }, // AH42 - 訓練実施期間（最終日）年
  trainingEndMonth: { row: 42, col: 41 }, // AN42 - 訓練実施期間（最終日）月
  trainingEndDay: { row: 42, col: 47 }, // AT42 - 訓練実施期間（最終日）日
  trainingLocationName: { row: 46, col: 11 }, // K46 - 実施場所名称
  trainingLocationAddress: { row: 47, col: 11 }, // K47 - 実施場所所在地

  // 訓練の実施方法
  trainingMethodClassroom: { row: 48, col: 12 }, // L48 - ①通学制
  trainingMethodInteractive: { row: 48, col: 27 }, // AA48 - ②同時双方向型の通信訓練
  trainingMethodElearning: { row: 48, col: 42 }, // AP48 - ③eラーニング
  trainingMethodCorrespondence: { row: 48, col: 57 }, // BE48 - ④通信制

  // 訓練時間（スクリーンショット2枚目下部参照）
  totalHours: { row: 50, col: 20 }, // T50 - 総訓練時間数（a.総訓練時間数）
  academicHours: { row: 51, col: 20 }, // T51 - OFF-JT訓練時間数（b.OFF-JT実施訓練時間数）
  practicalHours: { row: 51, col: 43 }, // AS51 - OJT訓練時間数（c.OJT実施訓練時間数）

  // 参加者数（スクリーンショット2枚目右上）
  participantsTotal: { row: 41, col: 40 }, // AN41 - 受講者（予定）合計
  participantsMale: { row: 73, col: 19 }, // S73 - 受講者（予定）男性
  participantsFemale: { row: 73, col: 40 }, // AN73 - 受講者（予定）女性

  // 定額制サービスの契約期間（分割）
  subscriptionStartYear: { row: 43, col: 14 }, // N43 - 契約期間（初日）年
  subscriptionStartMonth: { row: 43, col: 20 }, // T43 - 契約期間（初日）月
  subscriptionStartDay: { row: 43, col: 26 }, // Z43 - 契約期間（初日）日
  subscriptionEndYear: { row: 43, col: 34 }, // AH43 - 契約期間（最終日）年
  subscriptionEndMonth: { row: 43, col: 40 }, // AN43 - 契約期間（最終日）月
  subscriptionEndDay: { row: 43, col: 46 }, // AT43 - 契約期間（最終日）日

  // 資格試験（分割）
  examDetails: { row: 45, col: 15 }, // O45 - 資格試験の詳細
  examYear: { row: 45, col: 37 }, // AK45 - 受験日 年
  examMonth: { row: 45, col: 43 }, // AQ45 - 受験日 月
  examDay: { row: 45, col: 49 }, // AW45 - 受験日 日

  // OFF-JT訓練種別（行59）
  offJtInternalInsideInstructor: { row: 59, col: 12 }, // M59 - 事業内訓練（部内講師）
  offJtInternalOutsideInstructor: { row: 59, col: 26 }, // AA59 - 事業内訓練（部外講師）
  offJtExternal: { row: 59, col: 40 }, // AO59 - 事業外訓練

  // OFF-JT講師の氏名（行60）
  instructorNames: { row: 60, col: 11 }, // B60 - 講師氏名

  // 教育訓練機関の詳細情報（行61-71）
  institutionName: { row: 61, col: 18 }, // K61 - 教育訓練機関の名称
  institutionRepresentative: { row: 61, col: 39 }, // AF61 - 代表者名
  institutionAddress: { row: 62, col: 18 }, // K62 - 所在地
  contractBackground: { row: 63, col: 18 }, // K63 - 訓練契約締結経緯
  selectionTriggerSales: { row: 65, col: 14 }, // O65 - 営業を受けた
  selectionTriggerSearch: { row: 66, col: 14 }, // O66 - 自ら検索
  selectionTriggerOther: { row: 67, col: 14 }, // O67 - その他
  selectionTriggerOtherText: { row: 67, col: 19 }, // R67 - その他詳細
  loadReductionProposal: { row: 68, col: 12 }, // L68 - 負担軽減提案
  advertisingConfirmation: { row: 69, col: 14 }, // O69 - 広告宣伝確認
  materialConfirmation: { row: 70, col: 14 }, // O70 - 説明資料確認
  paymentConfirmation: { row: 71, col: 14 }, // O71 - 金銭提供なし確認

  // デジタル人材の育成を目的に行う訓練の場合（行72）
  digitalTrainingBusinessArchitect: { row: 72, col: 11 }, // K72 - ①ビジネスアーキテクト関係
  digitalTrainingDataScientist: { row: 72, col: 18 }, // R72 - ②データサイエンティスト関係
  digitalTrainingSoftwareEngineer: { row: 72, col: 25 }, // Y72 - ③ソフトウェアエンジニア関係
  digitalTrainingCyberSecurity: { row: 72, col: 32 }, // AF72 - ④サイバーセキュリティ関係
  digitalTrainingDesigner: { row: 72, col: 39 }, // AM72 - ⑤デザイナー関係
  digitalTrainingOther: { row: 72, col: 46 }, // AT72 - ⑥その他のデジタル人材関係

  // キャリア形成・学び直し支援センター名（行75）
  careerCenterName: { row: 75, col: 28 }, // AB75 - センター名

  // キャリアアップ計画（行83-84）
  hasCareerUpManager: { row: 83, col: 12 }, // L83-M83 - キャリアアップ管理者チェックボックス
  careerUpManagerName: { row: 83, col: 17 }, // Q83 - キャリアアップ管理者氏名
  hasCareerUpPlan: { row: 84, col: 12 }, // L84-M84 - キャリアアップ計画チェックボックス
  careerUpPlanYear: { row: 84, col: 39 }, // AM84 - 実施予定年
  careerUpPlanMonth: { row: 84, col: 44 }, // AR84 - 実施予定月

  // 助成区分のチェックボックス（スクリーンショットに基づく更新）
  // 人材育成支援コース
  humanResourcesTraining: { row: 32, col: 12 }, // L32 - ①人材育成訓練
  certifiedTraining: { row: 32, col: 27 }, // AA32 - ②認定実習併用職業訓練
  itBasicTraining: { row: 33, col: 12 }, // L33 - ③ＩＴ有期実習型訓練（基本型）
  careerUpTraining: { row: 33, col: 27 }, // AA33 - ④有期実習型訓練（キャリアップ型）
  dispatchTraining: { row: 33, col: 42 }, // AP33 - ⑤有期実習型訓練（派遣活用型）

  // 人への投資促進コース
  subscriptionTraining: { row: 35, col: 12 }, // L35 - ①定額制訓練
  voluntaryTraining: { row: 35, col: 27 }, // AA35 - ②自発的職業能力開発訓練（定額制サービスによる訓練以外）
  advancedDigitalTraining: { row: 36, col: 12 }, // L36 - ③高度デジタル人材訓練
  growthFieldTraining: { row: 36, col: 27 }, // AA36 - ④成長分野等人材訓練
  itFieldTraining: { row: 36, col: 42 }, // AP36 - ⑤情報技術分野野認定実習併用職業訓練

  // 事業展開等リスキリング支援コース（メイン項目 + a/bサブ項目）
  businessExpansionTrainingA: { row: 39, col: 12 }, // L39 - ①事業展開に伴い実施する訓練 a.定額制サービスによる訓練以外
  businessExpansionTrainingB: { row: 39, col: 27 }, // AA39 - ①事業展開に伴い実施する訓練 b.定額制サービスによる訓練
  dxGxAdvanceTrainingA: { row: 39, col: 12 }, // L39 - ②企業のDX・GXアドバンスワーカー a.定額制サービスによる訓練以外
  dxGxAdvanceTrainingB: { row: 39, col: 27 }, // AA39 - ②企業のDX・GXアドバンスワーカー b.定額制サービスによる訓練
  greenCarbonTrainingA: { row: 39, col: 12 }, // L39 - ③企業のグリーン・カーボンニュートラル化に伴い実施する訓練 a.定額制サービスによる訓練以外
  greenCarbonTrainingB: { row: 39, col: 27 }, // AA39 - ③企業のグリーン・カーボンニュートラル化に伴い実施する訓練 b.定額制サービスによる訓練
};

// 対象労働者一覧のセルマッピング
const WORKER_LIST_CELLS = {
  companyName: { row: 8, col: 3 }, // A8
  trainingCourseName: { row: 9, col: 3 }, // A9

  // 労働者情報の開始行
  workerStartRow: 13,
  workerRowSpan: 2, // 各労働者は2行使用
};

// 職業訓練実施計画届にデータを書き込む
export async function writeTrainingPlan(formData: FormData): Promise<ArrayBuffer> {
  try {
    // ログクリア
    localStorage.removeItem('excelUtils_logs');
    logToFile('=== Excel Processing Started ===');
    logToFile('Fetching template file...');
    const response = await fetch('/templates/training_plan.xlsx');
    if (!response.ok) {
      throw new Error(`Template file not found: ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();

    console.log('Loading workbook...');
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);

    console.log('Available worksheets:', workbook.worksheets.map(ws => ws.name));

    // ワークシート名を正確に取得
    const worksheet = workbook.worksheets[0]; // 最初のワークシートを使用
    if (!worksheet) throw new Error('ワークシートが見つかりません');

    console.log('Using worksheet:', worksheet.name);

    console.log('Writing data to cells...');

    // 事業主情報の書き込み（修正指示①）
    worksheet.getCell(TRAINING_PLAN_CELLS.companyNameMain.row, TRAINING_PLAN_CELLS.companyNameMain.col).value = formData.companyName;
    worksheet.getCell(TRAINING_PLAN_CELLS.companyAddressMain.row, TRAINING_PLAN_CELLS.companyAddressMain.col).value = formData.address;

    // 事業主の詳細情報の書き込み（新しいスクリーンショットに基づく）
    const businessOwnerPostalParts = formData.businessOwner.postalCode.split('-');
    if (businessOwnerPostalParts.length === 2) {
      worksheet.getCell(TRAINING_PLAN_CELLS.businessOwnerPostalCode1.row, TRAINING_PLAN_CELLS.businessOwnerPostalCode1.col).value = businessOwnerPostalParts[0];
      worksheet.getCell(TRAINING_PLAN_CELLS.businessOwnerPostalCode2.row, TRAINING_PLAN_CELLS.businessOwnerPostalCode2.col).value = businessOwnerPostalParts[1];
    }
    worksheet.getCell(TRAINING_PLAN_CELLS.businessOwnerCompanyName.row, TRAINING_PLAN_CELLS.businessOwnerCompanyName.col).value = formData.businessOwner.companyName;
    worksheet.getCell(TRAINING_PLAN_CELLS.businessOwnerRepresentativeName.row, TRAINING_PLAN_CELLS.businessOwnerRepresentativeName.col).value = formData.businessOwner.representativeName;
    worksheet.getCell(TRAINING_PLAN_CELLS.businessOwnerCorporateNumber.row, TRAINING_PLAN_CELLS.businessOwnerCorporateNumber.col).value = formData.businessOwner.corporateNumber;

    // 雇用保険適用事業所情報の書き込み
    worksheet.getCell(TRAINING_PLAN_CELLS.companyName.row, TRAINING_PLAN_CELLS.companyName.col).value = formData.companyName;

    // 雇用保険適用事業所番号を3つに分割（修正指示①）
    const insuranceParts = formData.insuranceNumber.split('-');
    if (insuranceParts.length >= 1) {
      worksheet.getCell(TRAINING_PLAN_CELLS.insuranceNumber1.row, TRAINING_PLAN_CELLS.insuranceNumber1.col).value = insuranceParts[0];
    }
    if (insuranceParts.length >= 2) {
      worksheet.getCell(TRAINING_PLAN_CELLS.insuranceNumber2.row, TRAINING_PLAN_CELLS.insuranceNumber2.col).value = insuranceParts[1];
    }
    if (insuranceParts.length >= 3) {
      worksheet.getCell(TRAINING_PLAN_CELLS.insuranceNumber3.row, TRAINING_PLAN_CELLS.insuranceNumber3.col).value = insuranceParts[2];
    }

    // 郵便番号の分割
    const postalParts = formData.postalCode.split('-');
    if (postalParts.length === 2) {
      worksheet.getCell(TRAINING_PLAN_CELLS.postalCode1.row, TRAINING_PLAN_CELLS.postalCode1.col).value = postalParts[0];
      worksheet.getCell(TRAINING_PLAN_CELLS.postalCode2.row, TRAINING_PLAN_CELLS.postalCode2.col).value = postalParts[1];
    }

    worksheet.getCell(TRAINING_PLAN_CELLS.address.row, TRAINING_PLAN_CELLS.address.col).value = formData.address;

    // 担当者情報の書き込み
    worksheet.getCell(TRAINING_PLAN_CELLS.contactName.row, TRAINING_PLAN_CELLS.contactName.col).value = formData.contactPerson.name;
    worksheet.getCell(TRAINING_PLAN_CELLS.contactDepartment.row, TRAINING_PLAN_CELLS.contactDepartment.col).value = formData.contactPerson.department;

    // 電話番号を3つに分割（修正指示③、スクリーンショット確認済み）
    const phoneParts = formData.contactPerson.phone.split('-');
    console.log('Phone parts:', phoneParts);
    console.log('Phone cell positions:',
      'R30:', TRAINING_PLAN_CELLS.contactPhone1.row, TRAINING_PLAN_CELLS.contactPhone1.col,
      'W30:', TRAINING_PLAN_CELLS.contactPhone2.row, TRAINING_PLAN_CELLS.contactPhone2.col,
      'AB30:', TRAINING_PLAN_CELLS.contactPhone3.row, TRAINING_PLAN_CELLS.contactPhone3.col
    );
    // 電話番号を分割して入力（既にうまくいっているので元の形式に戻す）
    if (phoneParts.length >= 1) {
      worksheet.getCell(TRAINING_PLAN_CELLS.contactPhone1.row, TRAINING_PLAN_CELLS.contactPhone1.col).value = phoneParts[0];
    }
    if (phoneParts.length >= 2) {
      worksheet.getCell(TRAINING_PLAN_CELLS.contactPhone2.row, TRAINING_PLAN_CELLS.contactPhone2.col).value = phoneParts[1];
    }
    if (phoneParts.length >= 3) {
      worksheet.getCell(TRAINING_PLAN_CELLS.contactPhone3.row, TRAINING_PLAN_CELLS.contactPhone3.col).value = phoneParts[2];
    }

    // メールアドレスをMAIL欄に（修正指示②）
    logToFile('Setting email to AM30: ' + formData.contactPerson.email);
    logToFile('Email cell position: ' + TRAINING_PLAN_CELLS.contactEmail.row + ', ' + TRAINING_PLAN_CELLS.contactEmail.col);

    try {
      // セルを明示的にクリアしてからメールアドレスを設定
      const emailCell = worksheet.getCell(TRAINING_PLAN_CELLS.contactEmail.row, TRAINING_PLAN_CELLS.contactEmail.col);
      logToFile('Current AM30 cell value before setting: ' + emailCell.value);
      logToFile('Current AM30 cell formula before setting: ' + emailCell.formula);
      logToFile('Current AM30 cell type before setting: ' + emailCell.type);

      // 値を直接設定（数式は自動的に上書きされる）
      emailCell.value = formData.contactPerson.email;

      logToFile('AM30 cell value after setting: ' + emailCell.value);
      logToFile('AM30 cell formula after setting: ' + emailCell.formula);
    } catch (emailError) {
      logToFile('ERROR in email setting: ' + emailError);
    }

    // 訓練情報の書き込み
    worksheet.getCell(TRAINING_PLAN_CELLS.trainingCourseName.row, TRAINING_PLAN_CELLS.trainingCourseName.col).value = formData.trainingCourseName;

    // 訓練実施期間（初日）を年月日に分割して書き込み
    const startDate = new Date(formData.trainingPeriod.startDate);
    worksheet.getCell(TRAINING_PLAN_CELLS.trainingStartYear.row, TRAINING_PLAN_CELLS.trainingStartYear.col).value = startDate.getFullYear();
    worksheet.getCell(TRAINING_PLAN_CELLS.trainingStartMonth.row, TRAINING_PLAN_CELLS.trainingStartMonth.col).value = startDate.getMonth() + 1;
    worksheet.getCell(TRAINING_PLAN_CELLS.trainingStartDay.row, TRAINING_PLAN_CELLS.trainingStartDay.col).value = startDate.getDate();

    // 訓練実施期間（最終日）を年月日に分割して書き込み
    const endDate = new Date(formData.trainingPeriod.endDate);
    worksheet.getCell(TRAINING_PLAN_CELLS.trainingEndYear.row, TRAINING_PLAN_CELLS.trainingEndYear.col).value = endDate.getFullYear();
    worksheet.getCell(TRAINING_PLAN_CELLS.trainingEndMonth.row, TRAINING_PLAN_CELLS.trainingEndMonth.col).value = endDate.getMonth() + 1;
    worksheet.getCell(TRAINING_PLAN_CELLS.trainingEndDay.row, TRAINING_PLAN_CELLS.trainingEndDay.col).value = endDate.getDate();

    // 定額制サービスの契約期間（該当する場合）
    if (formData.subscriptionPeriod) {
      const subStartDate = new Date(formData.subscriptionPeriod.startDate);
      worksheet.getCell(TRAINING_PLAN_CELLS.subscriptionStartYear.row, TRAINING_PLAN_CELLS.subscriptionStartYear.col).value = subStartDate.getFullYear();
      worksheet.getCell(TRAINING_PLAN_CELLS.subscriptionStartMonth.row, TRAINING_PLAN_CELLS.subscriptionStartMonth.col).value = subStartDate.getMonth() + 1;
      worksheet.getCell(TRAINING_PLAN_CELLS.subscriptionStartDay.row, TRAINING_PLAN_CELLS.subscriptionStartDay.col).value = subStartDate.getDate();

      const subEndDate = new Date(formData.subscriptionPeriod.endDate);
      worksheet.getCell(TRAINING_PLAN_CELLS.subscriptionEndYear.row, TRAINING_PLAN_CELLS.subscriptionEndYear.col).value = subEndDate.getFullYear();
      worksheet.getCell(TRAINING_PLAN_CELLS.subscriptionEndMonth.row, TRAINING_PLAN_CELLS.subscriptionEndMonth.col).value = subEndDate.getMonth() + 1;
      worksheet.getCell(TRAINING_PLAN_CELLS.subscriptionEndDay.row, TRAINING_PLAN_CELLS.subscriptionEndDay.col).value = subEndDate.getDate();
    }

    worksheet.getCell(TRAINING_PLAN_CELLS.trainingLocationName.row, TRAINING_PLAN_CELLS.trainingLocationName.col).value = formData.trainingLocation.name;
    worksheet.getCell(TRAINING_PLAN_CELLS.trainingLocationAddress.row, TRAINING_PLAN_CELLS.trainingLocationAddress.col).value = formData.trainingLocation.address;

    // 訓練の実施方法に✓を入れる
    switch (formData.trainingMethod) {
      case '通学制':
        worksheet.getCell(TRAINING_PLAN_CELLS.trainingMethodClassroom.row, TRAINING_PLAN_CELLS.trainingMethodClassroom.col).value = '✓';
        break;
      case '同時双方向型の通信訓練':
        worksheet.getCell(TRAINING_PLAN_CELLS.trainingMethodInteractive.row, TRAINING_PLAN_CELLS.trainingMethodInteractive.col).value = '✓';
        break;
      case 'eラーニング':
        worksheet.getCell(TRAINING_PLAN_CELLS.trainingMethodElearning.row, TRAINING_PLAN_CELLS.trainingMethodElearning.col).value = '✓';
        break;
      case '通信制':
        worksheet.getCell(TRAINING_PLAN_CELLS.trainingMethodCorrespondence.row, TRAINING_PLAN_CELLS.trainingMethodCorrespondence.col).value = '✓';
        break;
    }

    // 訓練時間の書き込み
    worksheet.getCell(TRAINING_PLAN_CELLS.totalHours.row, TRAINING_PLAN_CELLS.totalHours.col).value = formData.trainingHours.total;
    worksheet.getCell(TRAINING_PLAN_CELLS.academicHours.row, TRAINING_PLAN_CELLS.academicHours.col).value = formData.trainingHours.academic;
    worksheet.getCell(TRAINING_PLAN_CELLS.practicalHours.row, TRAINING_PLAN_CELLS.practicalHours.col).value = formData.trainingHours.practical;

    // 参加者数の書き込み
    const totalParticipants = Number(formData.participants.male) + Number(formData.participants.female);
    worksheet.getCell(TRAINING_PLAN_CELLS.participantsTotal.row, TRAINING_PLAN_CELLS.participantsTotal.col).value = totalParticipants;
    worksheet.getCell(TRAINING_PLAN_CELLS.participantsMale.row, TRAINING_PLAN_CELLS.participantsMale.col).value = formData.participants.male;
    worksheet.getCell(TRAINING_PLAN_CELLS.participantsFemale.row, TRAINING_PLAN_CELLS.participantsFemale.col).value = formData.participants.female;

    // 資格試験（該当する場合）
    if (formData.hasQualificationExam) {
      // 資格試験の詳細
      if (formData.qualificationExamDetails) {
        worksheet.getCell(TRAINING_PLAN_CELLS.examDetails.row, TRAINING_PLAN_CELLS.examDetails.col).value = formData.qualificationExamDetails;
      }
      // 資格試験受験日
      if (formData.qualificationExamDate) {
        const examDate = new Date(formData.qualificationExamDate);
        worksheet.getCell(TRAINING_PLAN_CELLS.examYear.row, TRAINING_PLAN_CELLS.examYear.col).value = examDate.getFullYear();
        worksheet.getCell(TRAINING_PLAN_CELLS.examMonth.row, TRAINING_PLAN_CELLS.examMonth.col).value = examDate.getMonth() + 1;
        worksheet.getCell(TRAINING_PLAN_CELLS.examDay.row, TRAINING_PLAN_CELLS.examDay.col).value = examDate.getDate();
      }
    }

    // OFF-JT訓練種別の書き込み
    switch (formData.offJtType) {
      case '事業内（部内講師）':
        worksheet.getCell(TRAINING_PLAN_CELLS.offJtInternalInsideInstructor.row, TRAINING_PLAN_CELLS.offJtInternalInsideInstructor.col).value = '✓';
        break;
      case '事業内（部外講師）':
        worksheet.getCell(TRAINING_PLAN_CELLS.offJtInternalOutsideInstructor.row, TRAINING_PLAN_CELLS.offJtInternalOutsideInstructor.col).value = '✓';
        break;
      case '事業外':
        worksheet.getCell(TRAINING_PLAN_CELLS.offJtExternal.row, TRAINING_PLAN_CELLS.offJtExternal.col).value = '✓';
        break;
    }

    // OFF-JT講師の氏名（事業内の場合）
    if (formData.instructorNames && (formData.offJtType === '事業内（部内講師）' || formData.offJtType === '事業内（部外講師）')) {
      worksheet.getCell(TRAINING_PLAN_CELLS.instructorNames.row, TRAINING_PLAN_CELLS.instructorNames.col).value = formData.instructorNames;
    }

    // 教育訓練機関の詳細情報（事業外の場合）
    if (formData.offJtType === '事業外' && formData.trainingInstitution) {
      const institution = formData.trainingInstitution;

      // 基本情報
      worksheet.getCell(TRAINING_PLAN_CELLS.institutionName.row, TRAINING_PLAN_CELLS.institutionName.col).value = institution.name;
      worksheet.getCell(TRAINING_PLAN_CELLS.institutionRepresentative.row, TRAINING_PLAN_CELLS.institutionRepresentative.col).value = institution.representativeName;
      worksheet.getCell(TRAINING_PLAN_CELLS.institutionAddress.row, TRAINING_PLAN_CELLS.institutionAddress.col).value = institution.address;

      // 訓練選択のきっかけ
      switch (institution.selectionTrigger) {
        case '営業を受けた':
          worksheet.getCell(TRAINING_PLAN_CELLS.selectionTriggerSales.row, TRAINING_PLAN_CELLS.selectionTriggerSales.col).value = '✓';
          break;
        case '自ら検索':
          worksheet.getCell(TRAINING_PLAN_CELLS.selectionTriggerSearch.row, TRAINING_PLAN_CELLS.selectionTriggerSearch.col).value = '✓';
          break;
        case 'その他':
          worksheet.getCell(TRAINING_PLAN_CELLS.selectionTriggerOther.row, TRAINING_PLAN_CELLS.selectionTriggerOther.col).value = '✓';
          if (institution.selectionTriggerOther) {
            worksheet.getCell(TRAINING_PLAN_CELLS.selectionTriggerOtherText.row, TRAINING_PLAN_CELLS.selectionTriggerOtherText.col).value = institution.selectionTriggerOther;
          }
          break;
      }


      // 確認事項
      if (institution.advertisingConfirmation) {
        worksheet.getCell(TRAINING_PLAN_CELLS.advertisingConfirmation.row, TRAINING_PLAN_CELLS.advertisingConfirmation.col).value = '✓';
      }
      if (institution.materialConfirmation) {
        worksheet.getCell(TRAINING_PLAN_CELLS.materialConfirmation.row, TRAINING_PLAN_CELLS.materialConfirmation.col).value = '✓';
      }
      if (institution.paymentConfirmation) {
        worksheet.getCell(TRAINING_PLAN_CELLS.paymentConfirmation.row, TRAINING_PLAN_CELLS.paymentConfirmation.col).value = '✓';
      }
    }

    // デジタル人材の育成を目的に行う訓練の場合
    if (formData.digitalTrainingCategory) {
      switch (formData.digitalTrainingCategory) {
        case 'businessArchitect':
          worksheet.getCell(TRAINING_PLAN_CELLS.digitalTrainingBusinessArchitect.row, TRAINING_PLAN_CELLS.digitalTrainingBusinessArchitect.col).value = '✓';
          break;
        case 'dataScientist':
          worksheet.getCell(TRAINING_PLAN_CELLS.digitalTrainingDataScientist.row, TRAINING_PLAN_CELLS.digitalTrainingDataScientist.col).value = '✓';
          break;
        case 'softwareEngineer':
          worksheet.getCell(TRAINING_PLAN_CELLS.digitalTrainingSoftwareEngineer.row, TRAINING_PLAN_CELLS.digitalTrainingSoftwareEngineer.col).value = '✓';
          break;
        case 'cyberSecurity':
          worksheet.getCell(TRAINING_PLAN_CELLS.digitalTrainingCyberSecurity.row, TRAINING_PLAN_CELLS.digitalTrainingCyberSecurity.col).value = '✓';
          break;
        case 'designer':
          worksheet.getCell(TRAINING_PLAN_CELLS.digitalTrainingDesigner.row, TRAINING_PLAN_CELLS.digitalTrainingDesigner.col).value = '✓';
          break;
        case 'other':
          worksheet.getCell(TRAINING_PLAN_CELLS.digitalTrainingOther.row, TRAINING_PLAN_CELLS.digitalTrainingOther.col).value = '✓';
          break;
      }
    }

    // キャリア形成・学び直し支援センター名
    if (formData.careerCenterName) {
      worksheet.getCell(TRAINING_PLAN_CELLS.careerCenterName.row, TRAINING_PLAN_CELLS.careerCenterName.col).value = formData.careerCenterName;
    }
    
    // キャリアアップ計画
    if (formData.careerUpPlan) {
      const plan = formData.careerUpPlan;
      
      // キャリアアップ管理者
      if (plan.hasCareerUpManager) {
        worksheet.getCell(TRAINING_PLAN_CELLS.hasCareerUpManager.row, TRAINING_PLAN_CELLS.hasCareerUpManager.col).value = '✓';
        worksheet.getCell(TRAINING_PLAN_CELLS.careerUpManagerName.row, TRAINING_PLAN_CELLS.careerUpManagerName.col).value = plan.careerUpManagerName;
      }
      
      // キャリアアップ計画（正社員化コース）
      if (plan.hasCareerUpPlan) {
        worksheet.getCell(TRAINING_PLAN_CELLS.hasCareerUpPlan.row, TRAINING_PLAN_CELLS.hasCareerUpPlan.col).value = '✓';
        worksheet.getCell(TRAINING_PLAN_CELLS.careerUpPlanYear.row, TRAINING_PLAN_CELLS.careerUpPlanYear.col).value = plan.implementationYear;
        worksheet.getCell(TRAINING_PLAN_CELLS.careerUpPlanMonth.row, TRAINING_PLAN_CELLS.careerUpPlanMonth.col).value = plan.implementationMonth;
      }
    }

    // 助成区分の✓マーク（スクリーンショットに基づく更新）
    const subsidyCheckboxes = {
      // 人材育成支援コース
      '人材育成訓練': TRAINING_PLAN_CELLS.humanResourcesTraining,
      '認定実習併用職業訓練': TRAINING_PLAN_CELLS.certifiedTraining,
      'ＩＴ有期実習型訓練（基本型）': TRAINING_PLAN_CELLS.itBasicTraining,
      '有期実習型訓練（キャリアップ型）': TRAINING_PLAN_CELLS.careerUpTraining,
      '有期実習型訓練（派遣活用型）': TRAINING_PLAN_CELLS.dispatchTraining,
      // 人への投資促進コース
      '定額制訓練': TRAINING_PLAN_CELLS.subscriptionTraining,
      '自発的職業能力開発訓練（定額制サービスによる訓練以外）': TRAINING_PLAN_CELLS.voluntaryTraining,
      '高度デジタル人材訓練': TRAINING_PLAN_CELLS.advancedDigitalTraining,
      '成長分野等人材訓練': TRAINING_PLAN_CELLS.growthFieldTraining,
      '情報技術分野野認定実習併用職業訓練': TRAINING_PLAN_CELLS.itFieldTraining,
      // 事業展開等リスキリング支援コース
      '事業展開に伴い実施する訓練 a.定額制サービスによる訓練以外': TRAINING_PLAN_CELLS.businessExpansionTrainingA,
      '事業展開に伴い実施する訓練 b.定額制サービスによる訓練': TRAINING_PLAN_CELLS.businessExpansionTrainingB,
      '企業のDX・GXアドバンスワーカー a.定額制サービスによる訓練以外': TRAINING_PLAN_CELLS.dxGxAdvanceTrainingA,
      '企業のDX・GXアドバンスワーカー b.定額制サービスによる訓練': TRAINING_PLAN_CELLS.dxGxAdvanceTrainingB,
      '企業のグリーン・カーボンニュートラル化に伴い実施する訓練 a.定額制サービスによる訓練以外': TRAINING_PLAN_CELLS.greenCarbonTrainingA,
      '企業のグリーン・カーボンニュートラル化に伴い実施する訓練 b.定額制サービスによる訓練': TRAINING_PLAN_CELLS.greenCarbonTrainingB,
    };

    // 選択された助成区分のセルに✓を入れる
    Object.entries(subsidyCheckboxes).forEach(([category, cellInfo]) => {
      if (formData.subsidyCategory === category) {
        logToFile(`Setting checkmark for ${category} at row ${cellInfo.row}, col ${cellInfo.col}`);
        worksheet.getCell(cellInfo.row, cellInfo.col).value = '✓';

        // 事業展開等リスキリング支援コースの場合、メイン項目にも✓を入れる
        if (category.includes('事業展開に伴い実施する訓練')) {
          worksheet.getCell(38, 12).value = '✓'; // ①事業展開に伴い実施する訓練
        } else if (category.includes('企業のDX・GXアドバンスワーカー')) {
          worksheet.getCell(38, 27).value = '✓'; // ②企業のDX・GXアドバンスワーカー
        } else if (category.includes('企業のグリーン・カーボンニュートラル化')) {
          worksheet.getCell(38, 42).value = '✓'; // ③企業のグリーン・カーボンニュートラル化
        }
      }
    });

    console.log('Generating buffer...');
    const buffer = await workbook.xlsx.writeBuffer();
    console.log('Success!');
    return buffer;
  } catch (error) {
    console.error('Error in writeTrainingPlan:', error);
    throw error;
  }
}

// 対象労働者一覧にデータを書き込む
export async function writeWorkerList(formData: FormData): Promise<ArrayBuffer> {
  try {
    console.log('Fetching worker list template...');
    const response = await fetch('/templates/worker_list.xlsx');
    if (!response.ok) {
      throw new Error(`Worker list template not found: ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();

    console.log('Loading worker list workbook...');
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);

    console.log('Available worksheets:', workbook.worksheets.map(ws => ws.name));

    // ワークシート名を正確に取得
    const worksheet = workbook.worksheets[0]; // 最初のワークシートを使用
    if (!worksheet) throw new Error('ワークシートが見つかりません');

    console.log('Using worksheet:', worksheet.name);

    console.log('Writing worker data...');
    // 基本情報の書き込み
    worksheet.getCell(WORKER_LIST_CELLS.companyName.row, WORKER_LIST_CELLS.companyName.col).value = formData.companyName;
    worksheet.getCell(WORKER_LIST_CELLS.trainingCourseName.row, WORKER_LIST_CELLS.trainingCourseName.col).value = formData.trainingCourseName;

    // 労働者情報の書き込み
    formData.workers.forEach((worker, index) => {
      const baseRow = WORKER_LIST_CELLS.workerStartRow + (index * WORKER_LIST_CELLS.workerRowSpan);

      // No
      worksheet.getCell(baseRow, 1).value = index + 1;

      // 氏名
      worksheet.getCell(baseRow, 2).value = worker.name;

      // 雇用保険被保険者番号
      worksheet.getCell(baseRow, 3).value = worker.insuranceNumber.part1;
      worksheet.getCell(baseRow, 6).value = worker.insuranceNumber.part2;
      worksheet.getCell(baseRow, 9).value = worker.insuranceNumber.part3;

      // 雇用形態（奇数行：正規雇用、偶数行：有期契約）
      if (worker.employmentType === '正規雇用労働者等') {
        worksheet.getCell(baseRow, 10).value = '○';
      } else {
        worksheet.getCell(baseRow + 1, 10).value = '○';
      }

      // 採用予定日（新規学卒予定者の場合）
      if (worker.workerAttribute === '新規学卒予定者' && worker.expectedHireDate) {
        worksheet.getCell(baseRow, 13).value = worker.expectedHireDate.year;
        worksheet.getCell(baseRow, 15).value = worker.expectedHireDate.month;
        worksheet.getCell(baseRow, 17).value = worker.expectedHireDate.day;
      }

      // 対象労働者の属性（奇数行：新規学卒予定者、偶数行：それ以外）
      if (worker.workerAttribute === '新規学卒予定者') {
        worksheet.getCell(baseRow, 19).value = '○';
      } else {
        worksheet.getCell(baseRow + 1, 19).value = '○';
      }

      // 所属事業主名（出向者等の場合）
      if (worker.affiliatedCompanyName) {
        worksheet.getCell(baseRow, 21).value = worker.affiliatedCompanyName;
      }
    });

    console.log('Generating worker list buffer...');
    const buffer = await workbook.xlsx.writeBuffer();
    console.log('Worker list success!');
    return buffer;
  } catch (error) {
    console.error('Error in writeWorkerList:', error);
    throw error;
  }
}
